import { Component, type ErrorInfo, type ReactNode } from "react";
import { GlobalErrorScreen } from "./GlobalErrorScreen";
import { logger } from "@/services/logger";
import { isChunkLoadError, reloadOnceForChunkError } from "@/utils";

/**
 * ErrorBoundary
 *
 * Top-level guard around the React tree. Catches render/lifecycle errors that
 * the router's own boundary can't (e.g. failures in providers mounted above
 * the router), logs them, and shows the graceful {@link GlobalErrorScreen}
 * with a full reload as recovery.
 *
 * @example
 *   <ErrorBoundary><App /></ErrorBoundary>
 */
interface Props {
  children: ReactNode;
}

interface State {
  error: unknown;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error("boundary", `Uncaught error: ${error.message}`, info.componentStack);
    if (isChunkLoadError(error)) {
      reloadOnceForChunkError();
    }
  }

  private readonly reload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.error) {
      return <GlobalErrorScreen error={this.state.error} onReset={this.reload} />;
    }
    return this.props.children;
  }
}
